package diana.soleil.design.iraniancanadienbazzar.feature.feed

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.PostRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.common.Formatters
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Comment
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Report lifecycle for a comment (mirrors the DS `ReportStatus`, with a sign-in gate for guests). */
enum class CommentReportStatus { Idle, Sending, Sent, SignInRequired, Failed }

/** Comments thread for one post: live comments + a composer. Reading is public; commenting needs auth. */
data class CommentsUiState(
    val loading: Boolean = true,
    val comments: List<Comment> = emptyList(),
    val error: Boolean = false,
    val posting: Boolean = false,
    /** Guest tried to comment — the caller routes them to sign-in. */
    val signInRequired: Boolean = false,
    val commentFailed: Boolean = false,
    /** Deleting the caller's own comment failed — the sheet shows an inline error. */
    val deleteFailed: Boolean = false,
    val reportStatus: CommentReportStatus = CommentReportStatus.Idle,
    /** "Ask the Mahaleh": the business this draft comment will recommend (null = plain comment). */
    val attachedBusiness: Business? = null,
    /** Live matches for the in-sheet business picker (metro-scoped, filtered by [pickerQuery]). */
    val pickerQuery: String = "",
    val pickerResults: List<Business> = emptyList(),
)

class CommentsViewModel(
    private val postRepository: PostRepository,
    private val reportRepository: ReportRepository,
    blockRepository: BlockRepository,
    businessRepository: BusinessRepository,
    cityController: CityController,
    private val postId: String,
) : ViewModel() {

    private val posting = MutableStateFlow(false)
    private val attachedBusiness = MutableStateFlow<Business?>(null)
    private val pickerQuery = MutableStateFlow("")
    private val pickerVisible = MutableStateFlow(false)

    // Metro-scoped corpus for the "recommend a business" picker, filtered as the asker types.
    // The corpus listener only exists WHILE the picker sheet is open — most comment threads never
    // open it, and unconditionally subscribing charged every thread-open the full metro directory.
    @OptIn(kotlinx.coroutines.ExperimentalCoroutinesApi::class)
    private val pickerResults =
        combine(
            pickerVisible.flatMapLatest { visible ->
                if (!visible) {
                    kotlinx.coroutines.flow.flowOf(DataResult.Success(emptyList<Business>()) as DataResult<List<Business>>)
                } else {
                    combine(cityController.activeMetroId, cityController.activeCountry) { m, c -> m to c }
                        .flatMapLatest { (metroId, country) ->
                            businessRepository.observeBusinesses(metroId = metroId, country = country.code)
                        }
                }
            },
            pickerQuery,
        ) { result, query ->
            val corpus = (result as? DataResult.Success)?.data.orEmpty()
            val normalized = Formatters.normalizePersian(query).trim().lowercase()
            if (normalized.isBlank()) corpus.take(PICKER_LIMIT)
            else corpus.filter { it.name.lowercase().contains(normalized) ||
                (it.nameLocalized?.fa ?: "").contains(normalized) }.take(PICKER_LIMIT)
        }
    private val gate = MutableStateFlow<AppError?>(null)
    private val deleteFailed = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(CommentReportStatus.Idle)

    // Live thread with blocked authors dropped (same client-side block filter as the feed).
    private val visibleComments =
        combine(postRepository.observeComments(postId), blockRepository.observeBlockedUids()) { result, blocked ->
            when (result) {
                is DataResult.Success -> DataResult.Success(result.data.filter { it.authorUid !in blocked })
                is DataResult.Failure -> result
            }
        }

    val state: StateFlow<CommentsUiState> =
        combine(
            combine(visibleComments, posting, gate, deleteFailed, reportStatus) { result, isPosting, error, deleteError, report ->
                when (result) {
                    is DataResult.Success -> CommentsUiState(
                        loading = false,
                        comments = result.data,
                        posting = isPosting,
                        signInRequired = error is AppError.Unauthorized,
                        commentFailed = error != null && error !is AppError.Unauthorized,
                        deleteFailed = deleteError,
                        reportStatus = report,
                    )
                    is DataResult.Failure -> CommentsUiState(loading = false, error = true)
                }
            },
            attachedBusiness,
            pickerQuery,
            pickerResults,
        ) { base, attached, query, results ->
            base.copy(attachedBusiness = attached, pickerQuery = query, pickerResults = results)
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), CommentsUiState())

    fun onPickerQueryChange(query: String) { pickerQuery.value = query }

    /** The screen reports the picker sheet's visibility so the corpus listener can attach/detach. */
    fun setPickerVisible(visible: Boolean) { pickerVisible.value = visible }

    fun attachBusiness(business: Business?) { attachedBusiness.value = business }

    fun submit(text: String) {
        if (text.isBlank() || posting.value) return
        viewModelScope.launch {
            posting.value = true
            gate.value = null
            when (val result = postRepository.addComment(postId, text, attachedBusiness.value)) {
                is DataResult.Success -> attachedBusiness.value = null
                is DataResult.Failure -> gate.value = result.error
            }
            posting.value = false
        }
    }

    fun consumeGate() {
        gate.value = null
    }

    /** Deletes the caller's own comment; the live thread drops it (and the Function recomputes the count). */
    fun deleteComment(commentId: String) {
        viewModelScope.launch {
            deleteFailed.value = false
            if (postRepository.deleteComment(postId, commentId) is DataResult.Failure) deleteFailed.value = true
        }
    }

    /** Files a moderation report against a comment; guests trip [CommentReportStatus.SignInRequired]. */
    fun reportComment(commentId: String, reason: String) {
        if (reportStatus.value == CommentReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = CommentReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("comment", commentId, reason)) {
                is DataResult.Success -> CommentReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) CommentReportStatus.SignInRequired
                    else CommentReportStatus.Failed
            }
        }
    }

    fun resetReport() {
        reportStatus.value = CommentReportStatus.Idle
    }

    private companion object {
        /** The picker is a quick shortlist, not a browse surface — search narrows it. */
        const val PICKER_LIMIT = 8
    }
}
